import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { opportunites, type OpportuniteType } from "@/mocks/opportunites";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

export default function OpportunitesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [activeFilter, setActiveFilter] = useState<OpportuniteType | "all">("all");
  const [search, setSearch] = useState("");

  useSeoMeta({
    title: "Opportunités pour entrepreneurs africains — Wazi Stories Afrique",
    description: "Trouvez les meilleures opportunités de financement, emplois et appels à projets pour entrepreneurs africains. Bourses, subventions et offres d'emploi en Afrique.",
    type: "website",
    keywords: "opportunité entrepreneur africain, financement startup Afrique, emploi Afrique, appel à projets Afrique, Wazi Stories",
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Opportunités pour entrepreneurs africains",
    "url": `${siteUrl}/opportunites`,
    "numberOfItems": opportunites.length,
    "itemListElement": opportunites.slice(0, 10).map((opp, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": opp.type === "job" ? "JobPosting" : "Grant",
        "name": opp.title,
        "description": opp.description,
        "hiringOrganization": {
          "@type": "Organization",
          "name": opp.organization
        },
        "url": opp.link,
        "validThrough": opp.deadline
      }
    }))
  }, "opportunites");

  const typeConfig: Record<OpportuniteType, { labelKey: string; icon: string; bg: string; text: string; border: string }> = {
    job:     { labelKey: "opportunites.type.job",     icon: "ri-briefcase-line",           bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    funding: { labelKey: "opportunites.type.funding", icon: "ri-money-dollar-circle-line", bg: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-200" },
    appel:   { labelKey: "opportunites.type.appel",   icon: "ri-trophy-line",              bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  };

  const filters: { key: OpportuniteType | "all"; labelKey: string; icon: string }[] = [
    { key: "all",     labelKey: "opportunites.filter.all.icon", icon: "ri-compass-3-line" },
    { key: "funding", labelKey: "opportunites.filter.funding",  icon: "ri-money-dollar-circle-line" },
    { key: "job",     labelKey: "opportunites.filter.job",      icon: "ri-briefcase-line" },
    { key: "appel",   labelKey: "opportunites.filter.appel",    icon: "ri-trophy-line" },
  ];

  const filtered = useMemo(() => {
    let data = activeFilter === "all" ? opportunites : opportunites.filter((o) => o.type === activeFilter);
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (o) =>
          o.title.toLowerCase().includes(q) ||
          o.organization.toLowerCase().includes(q) ||
          o.sector.toLowerCase().includes(q)
      );
    }
    return data;
  }, [activeFilter, search]);

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 md:pt-20 bg-gradient-to-br from-gray-900 to-gray-800 pb-8 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-8 md:pt-12">

          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-5 md:gap-6 mb-6 md:mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <i className="ri-compass-3-line text-orange-400 text-lg md:text-xl"></i>
                <span className="text-orange-400 text-xs md:text-sm font-bold uppercase tracking-wider">{t("section.opportunites.title")}</span>
              </div>
              <h1 className="text-2xl md:text-5xl font-bold text-white font-serif mb-2 md:mb-3">
                {t("opportunites.hero.title")}
              </h1>
              <p className="text-gray-400 text-sm md:text-base max-w-xl">{t("opportunites.hero.subtitle")}</p>
            </div>

            {/* Stats chips */}
            <div className="flex gap-3 md:gap-4 text-center flex-wrap">
              {[
                { value: opportunites.filter((o) => o.type === "funding").length, labelKey: "opportunites.stats.funding", color: "text-orange-400" },
                { value: opportunites.filter((o) => o.type === "job").length,     labelKey: "opportunites.stats.jobs",    color: "text-emerald-400" },
                { value: opportunites.filter((o) => o.type === "appel").length,   labelKey: "opportunites.stats.appels",  color: "text-amber-400" },
              ].map((s) => (
                <div key={s.labelKey} className="bg-white/10 rounded-2xl px-4 md:px-5 py-3 md:py-4 flex-1 min-w-[80px]">
                  <p className={`text-xl md:text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-gray-400 text-xs mt-1 whitespace-nowrap">{t(s.labelKey)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("opportunites.search.placeholder")}
              className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-gray-500 text-sm focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 md:px-4 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === f.key ? "bg-orange-500 text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"
                }`}
              >
                <i className={f.icon}></i>
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">
        <p className="text-sm text-gray-500 mb-4 md:mb-6">
          {filtered.length} {filtered.length > 1 ? t("opportunites.results_plural") : t("opportunites.results")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((opp) => {
            const cfg = typeConfig[opp.type];
            const daysLeft = Math.ceil((new Date(opp.deadline).getTime() - Date.now()) / 86400000);
            const isUrgent = daysLeft <= 14;
            return (
              <a
                key={opp.id}
                href={opp.link}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`group bg-white rounded-2xl border ${opp.featured ? "border-orange-200" : "border-gray-100"} hover:border-orange-300 hover:-translate-y-1 transition-all duration-300 p-4 md:p-5 cursor-pointer block`}
              >
                <div className="flex items-start justify-between gap-3 mb-3 md:mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={opp.logo} alt={opp.organization} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-700 line-clamp-1">{opp.organization}</p>
                      <p className="text-xs text-gray-400">{opp.countryFlag} {opp.country}</p>
                    </div>
                  </div>
                  <span className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                    <i className={cfg.icon}></i>
                    <span className="hidden sm:inline">{t(cfg.labelKey)}</span>
                  </span>
                </div>

                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-orange-500 transition-colors">{opp.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3 md:mb-4">{opp.description}</p>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                    {opp.amount && (
                      <span className="text-xs font-bold text-green-600 flex items-center gap-1">
                        <i className="ri-money-dollar-circle-line"></i>{opp.amount}
                      </span>
                    )}
                    {opp.remote && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-global-line"></i>{t("opportunites.remote")}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-semibold flex items-center gap-1 flex-shrink-0 ${isUrgent ? "text-red-500" : "text-gray-400"}`}>
                    <i className="ri-time-line"></i>
                    {isUrgent
                      ? `J-${daysLeft}`
                      : new Date(opp.deadline).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short" })}
                  </span>
                </div>
              </a>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 md:py-20 text-gray-400">
            <i className="ri-compass-3-line text-5xl mb-4 block"></i>
            <p className="text-lg font-medium">{t("opportunites.noResults")}</p>
          </div>
        )}

        {/* Submit CTA */}
        <div className="mt-8 md:mt-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 md:p-8 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl mb-2">{t("opportunites.cta.title")}</h3>
          <p className="text-white/80 text-sm mb-4 md:mb-5">{t("opportunites.cta.desc")}</p>
          <Link
            to="/soumettre"
            className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-5 md:px-6 py-2.5 md:py-3 rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-add-circle-line"></i>{t("opportunites.cta.btn")}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
